import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

// Support both predefined themes and custom themes (theme-custom-*)
type Theme = 'dark' | 'light' | 'system' | 'cdb-light' | 'cdb-dark' | 'theme-aqua' | 'theme-dark-pro' | 'theme-nordic' | 'theme-sunset' | 'theme-forest' | 'theme-ocean' | 'theme-monochrome' | 'theme-contrast-dark' | 'theme-contrast-light' | (string & {})

type ThemeProviderProps = {
  children: ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  )

  useEffect(() => {
    const root = window.document.documentElement
    const body = window.document.body
    const themeClasses = ['light', 'dark', 'cdb-light', 'cdb-dark', 'theme-aqua', 'theme-dark-pro', 'theme-nordic', 'theme-sunset', 'theme-forest', 'theme-ocean', 'theme-monochrome', 'theme-contrast-dark', 'theme-contrast-light']

    // Remove all theme classes from both root and body (including custom themes)
    // Remove known theme classes
    root.classList.remove(...themeClasses)
    body.classList.remove(...themeClasses)

    // Remove any custom theme classes (theme-custom-*)
    const rootClasses = Array.from(root.classList)
    const customThemeClasses = rootClasses.filter(c => c.startsWith('theme-custom-'))
    if (customThemeClasses.length > 0) {
      root.classList.remove(...customThemeClasses)
      body.classList.remove(...customThemeClasses)
    }

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light'

      root.classList.add(systemTheme)
      body.classList.add(systemTheme)
      root.setAttribute('data-theme', systemTheme)
      body.setAttribute('data-theme', systemTheme)

      // Force reflow to ensure styles are applied immediately
      void root.offsetHeight
      return
    }

    root.classList.add(theme)
    body.classList.add(theme)
    root.setAttribute('data-theme', theme)
    body.setAttribute('data-theme', theme)

    // Force reflow to ensure styles are applied immediately
    void root.offsetHeight
  }, [theme])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme)
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
